import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { formatPrice } from '../lib/currency';
import { Wifi, Zap, Globe, X } from 'lucide-react';

// Global eSIM cross-sell — pops the cheapest destination eSIM right after a traveller
// taps BOOK on a hotel or flight. Real live plans only (no demo); hidden if none cover
// the destination. Triggered via AppContext.promptEsim({ country_code, country, city }).
export function EsimCrossSellModal() {
  const { esimPrompt, closeEsimPrompt, currency } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!esimPrompt) { setData(null); return; }
    const cc = esimPrompt.country_code;
    if (!cc) { setData({ available: false }); return; }
    setLoading(true);
    api.esimCrossSell(cc)
      .then(({ data }) => setData(data))
      .catch(() => setData({ available: false }))
      .finally(() => setLoading(false));
  }, [esimPrompt]);

  if (!esimPrompt) return null;
  const country = data?.name || esimPrompt.country || esimPrompt.city || 'your destination';
  const plan = data?.cheapest;
  const slug = data?.slug;

  const goPlans = () => {
    closeEsimPrompt();
    if (slug) navigate(`/esim/${slug}`); else navigate('/esim');
  };

  return (
    <Dialog open={!!esimPrompt} onOpenChange={(o) => { if (!o) closeEsimPrompt(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0" data-testid="esim-crosssell-modal">
        <div className="bg-primary text-primary-foreground p-5">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-accent text-accent-foreground"><Wifi className="h-5 w-5" /></span>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-primary-foreground/70">Nice pick! One more thing…</p>
              <h3 className="font-display text-lg font-semibold leading-tight">Stay connected in {country}</h3>
            </div>
          </div>
          <p className="text-sm text-primary-foreground/80 mt-2">Skip roaming bills — grab a travel eSIM and get online the moment you land.</p>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="h-24 rounded-xl bg-secondary animate-pulse" data-testid="esim-crosssell-loading" />
          ) : data && data.available && plan ? (
            <>
              <div className="rounded-xl border border-border p-3 flex items-center justify-between gap-3" data-testid="esim-crosssell-plan">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{plan.data_amount} · {plan.validity_days} days</p>
                  <p className="text-xs text-muted-foreground truncate">{plan.provider_name} · instant delivery{data.count > 1 ? ` · ${data.count} plans` : ''}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-muted-foreground">from</p>
                  <p className="text-lg font-semibold tabular">{formatPrice(plan.price_inr, currency)}</p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Zap className="h-3 w-3" /> Instant QR</span>
                <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" /> Keep your number</span>
              </div>
              <Button onClick={goPlans} className="w-full h-11 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold mt-4" data-testid="esim-crosssell-cta">
                View {country} eSIM plans
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Browse travel eSIMs for 175+ countries and land connected.</p>
              <Button onClick={goPlans} className="w-full h-11 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold mt-4" data-testid="esim-crosssell-cta">
                Explore travel eSIMs
              </Button>
            </>
          )}
          <button onClick={closeEsimPrompt} className="w-full text-center text-xs text-muted-foreground hover:text-foreground mt-3" data-testid="esim-crosssell-dismiss">
            No thanks, continue to my booking
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
